import { useCallback, useMemo, useState } from 'react';
import { CommunityId } from '../model/community.types';
import { Post } from '../model/post.types';
import { useDeletePost } from '../api/useDeletePost';
import { usePinPostAction } from '../lib/usePinPostAction';
import { ActionMenu, ActionMenuItem } from './ActionMenu';
import { ActionConfirmModal } from './ActionConfirmModal';

type Props = {
  post: Post;
  communityId: CommunityId;
  influencerId?: string | number;
  pinnedCount: number;
};

export const OwnPostActionsMenu = ({
  post,
  communityId,
  influencerId,
  pinnedCount,
}: Props) => {
  const deletePost = useDeletePost();
  const pin = usePinPostAction(post, communityId, pinnedCount, influencerId);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const openDelete = useCallback(() => setConfirmingDelete(true), []);
  const dismiss = useCallback(() => setConfirmingDelete(false), []);
  const confirmDelete = useCallback(() => {
    deletePost({ communityId, postId: post.id });
    setConfirmingDelete(false);
  }, [deletePost, communityId, post.id]);

  const items = useMemo<ActionMenuItem[]>(
    () => [
      {
        key: 'pin',
        label: 'Pin to top',
        disabled: pin.disabled,
        onClick: pin.toggle,
        toggle: { checked: pin.checked, helperText: pin.helperText },
      },
      {
        key: 'delete',
        label: 'Delete post',
        destructive: true,
        onClick: openDelete,
      },
    ],
    [pin.disabled, pin.toggle, pin.checked, pin.helperText, openDelete],
  );

  return (
    <>
      <ActionMenu items={items} />
      <ActionConfirmModal
        open={confirmingDelete}
        title="Are you sure you want to delete this post?"
        actionLabel="Delete"
        onConfirm={confirmDelete}
        onDismiss={dismiss}
      />
    </>
  );
};
